'use client'

import { useEffect, useImperativeHandle, useRef, type Ref } from 'react'

// --- 2D simplex noise (seeded) ---

const GRAD = [
  [1, 1],
  [-1, 1],
  [1, -1],
  [-1, -1],
  [1, 0],
  [-1, 0],
  [0, 1],
  [0, -1],
  [1, 1],
  [-1, 1],
  [1, -1],
  [-1, -1],
]
const F2 = 0.5 * (Math.sqrt(3) - 1)
const G2 = (3 - Math.sqrt(3)) / 6

function buildPerm(seed: number): Uint8Array {
  const p = new Uint8Array(512)
  const base = new Uint8Array(256)
  for (let i = 0; i < 256; i++) base[i] = i
  // Fisher-Yates shuffle seeded with a simple LCG
  let s = seed | 0
  for (let i = 255; i > 0; i--) {
    s = (s * 1664525 + 1013904223) | 0
    const j = (s >>> 0) % (i + 1)
    const tmp = base[i]
    base[i] = base[j]
    base[j] = tmp
  }
  for (let i = 0; i < 512; i++) p[i] = base[i & 255]
  return p
}

function noise2D(perm: Uint8Array, x: number, y: number): number {
  const s = (x + y) * F2
  const i = Math.floor(x + s)
  const j = Math.floor(y + s)
  const t = (i + j) * G2
  const x0 = x - (i - t)
  const y0 = y - (j - t)
  const i1 = x0 > y0 ? 1 : 0
  const j1 = x0 > y0 ? 0 : 1
  const x1 = x0 - i1 + G2
  const y1 = y0 - j1 + G2
  const x2 = x0 - 1 + 2 * G2
  const y2 = y0 - 1 + 2 * G2
  const ii = i & 255
  const jj = j & 255

  let n0 = 0,
    n1 = 0,
    n2 = 0
  let t0 = 0.5 - x0 * x0 - y0 * y0
  if (t0 > 0) {
    t0 *= t0
    const g = GRAD[perm[ii + perm[jj]] % 12]
    n0 = t0 * t0 * (g[0] * x0 + g[1] * y0)
  }
  let t1 = 0.5 - x1 * x1 - y1 * y1
  if (t1 > 0) {
    t1 *= t1
    const g = GRAD[perm[ii + i1 + perm[jj + j1]] % 12]
    n1 = t1 * t1 * (g[0] * x1 + g[1] * y1)
  }
  let t2 = 0.5 - x2 * x2 - y2 * y2
  if (t2 > 0) {
    t2 *= t2
    const g = GRAD[perm[ii + 1 + perm[jj + 1]] % 12]
    n2 = t2 * t2 * (g[0] * x2 + g[1] * y2)
  }
  return 70 * (n0 + n1 + n2) // range approx -1..1
}

export interface TopoFlowHandle {
  triggerRipple: (duration?: number) => void
}

interface TopoFlowProps {
  seed: number
  lineCount?: number
  speed?: number
  scale?: number
  strokeColour?: string
  strokeWidth?: number
  className?: string
  ref?: Ref<TopoFlowHandle>
}

export function TopoFlow({
  seed,
  lineCount = 12,
  speed = 0.15,
  scale = 0.003,
  strokeColour = 'rgba(255,255,255,0.06)',
  strokeWidth = 1,
  className = '',
  ref,
}: TopoFlowProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const rippleUntilRef = useRef(0)
  const rippleStartRef = useRef(0)
  const rippleDurationRef = useRef(400)

  useImperativeHandle(ref, () => ({
    triggerRipple(duration = 400) {
      rippleStartRef.current = performance.now()
      rippleDurationRef.current = duration
      rippleUntilRef.current = performance.now() + duration
    },
  }))

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const perm = buildPerm(seed)
    let animId = 0
    let time = 0
    let lastFrame = performance.now()
    let isVisible = false

    function resize() {
      if (!canvas || !ctx) return
      // Cap DPR at 2 on small viewports so phones with 3x screens render
      // ~44% fewer pixels per frame without visibly losing line crispness.
      const dprCap = canvas.offsetWidth < 768 ? 2 : 3
      const dpr = Math.min(devicePixelRatio || 1, dprCap)
      canvas.width = canvas.offsetWidth * dpr
      canvas.height = canvas.offsetHeight * dpr
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    }

    // Defer animation until the canvas scrolls into view
    const visibilityObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          isVisible = true
          lastFrame = performance.now()
          animId = requestAnimationFrame(draw)
        } else if (!entry.isIntersecting && isVisible) {
          isVisible = false
          cancelAnimationFrame(animId)
        }
      },
      { rootMargin: '200px' }
    )
    visibilityObserver.observe(canvas)

    // Marching squares: generate a noise heightmap grid, then trace
    // isolines at evenly spaced thresholds to produce true contour lines
    // that curve around hills and valleys like a real topographic map.

    const isPhone = canvas.offsetWidth < 480
    const isMobile = canvas.offsetWidth < 768
    const cellSize = isPhone ? 18 : isMobile ? 14 : 8
    const lerp = (a: number, b: number, t: number) => a + (b - a) * t
    let frameSkip = 0

    function draw(now: number) {
      if (!canvas || !ctx) return

      // Mobile: skip every other frame for ~30fps
      if (isMobile) {
        frameSkip++
        if (frameSkip % 2 !== 0) {
          animId = requestAnimationFrame(draw)
          return
        }
      }

      const dt = now - lastFrame
      lastFrame = now

      // Ripple: briefly accelerate the terrain drift then settle back.
      // This makes contours flow faster during the hero text transition
      // without reshaping the terrain (which caused visual jumps).
      let speedMult = 1
      if (now < rippleUntilRef.current) {
        const elapsed = now - rippleStartRef.current
        const t = Math.min(elapsed / rippleDurationRef.current, 1)
        speedMult = 1 + 2 * Math.sin(t * Math.PI)
      }
      time += speed * (dt / 16.67) * speedMult

      const cssW = canvas.offsetWidth
      const cssH = canvas.offsetHeight
      ctx.clearRect(0, 0, cssW, cssH)

      const cols = Math.ceil(cssW / cellSize) + 1
      const rows = Math.ceil(cssH / cellSize) + 1
      const timeOffset = time * 0.002

      // Build heightmap grid from noise
      const grid = new Float32Array(cols * rows)
      for (let row = 0; row < rows; row++) {
        for (let col = 0; col < cols; col++) {
          const nx = col * cellSize * scale
          const ny = row * cellSize * scale + timeOffset
          grid[row * cols + col] = noise2D(perm, nx, ny)
        }
      }

      // Distribute threshold levels evenly across the noise range
      ctx.strokeStyle = strokeColour
      ctx.lineWidth = strokeWidth
      ctx.beginPath()

      for (let level = 0; level < lineCount; level++) {
        // Thresholds from -0.6 to 0.6 (central range of noise output)
        const threshold = -0.6 + (1.2 * (level + 1)) / (lineCount + 1)

        // March through each cell and draw contour segments
        for (let row = 0; row < rows - 1; row++) {
          for (let col = 0; col < cols - 1; col++) {
            const tl = grid[row * cols + col]
            const tr = grid[row * cols + col + 1]
            const br = grid[(row + 1) * cols + col + 1]
            const bl = grid[(row + 1) * cols + col]

            // Classify corners: 1 if above threshold, 0 if below
            const config =
              (tl >= threshold ? 8 : 0) |
              (tr >= threshold ? 4 : 0) |
              (br >= threshold ? 2 : 0) |
              (bl >= threshold ? 1 : 0)

            // Skip empty (0) and full (15) cells
            if (config === 0 || config === 15) continue

            const x = col * cellSize
            const y = row * cellSize

            // Interpolated edge positions
            const top = lerp(x, x + cellSize, (threshold - tl) / (tr - tl))
            const right = lerp(y, y + cellSize, (threshold - tr) / (br - tr))
            const bottom = lerp(x, x + cellSize, (threshold - bl) / (br - bl))
            const left = lerp(y, y + cellSize, (threshold - tl) / (bl - tl))

            // Draw line segments for each marching squares case
            switch (config) {
              case 1:
              case 14:
                ctx.moveTo(x, left)
                ctx.lineTo(bottom, y + cellSize)
                break
              case 2:
              case 13:
                ctx.moveTo(bottom, y + cellSize)
                ctx.lineTo(x + cellSize, right)
                break
              case 3:
              case 12:
                ctx.moveTo(x, left)
                ctx.lineTo(x + cellSize, right)
                break
              case 4:
              case 11:
                ctx.moveTo(top, y)
                ctx.lineTo(x + cellSize, right)
                break
              case 5:
                ctx.moveTo(x, left)
                ctx.lineTo(top, y)
                ctx.moveTo(bottom, y + cellSize)
                ctx.lineTo(x + cellSize, right)
                break
              case 6:
              case 9:
                ctx.moveTo(top, y)
                ctx.lineTo(bottom, y + cellSize)
                break
              case 7:
              case 8:
                ctx.moveTo(x, left)
                ctx.lineTo(top, y)
                break
              case 10:
                ctx.moveTo(x, left)
                ctx.lineTo(bottom, y + cellSize)
                ctx.moveTo(top, y)
                ctx.lineTo(x + cellSize, right)
                break
            }
          }
        }
      }

      ctx.stroke()
      if (isVisible) animId = requestAnimationFrame(draw)
    }

    resize()
    const resizeObserver = new ResizeObserver(resize)
    resizeObserver.observe(canvas)

    return () => {
      cancelAnimationFrame(animId)
      resizeObserver.disconnect()
      visibilityObserver.disconnect()
    }
  }, [seed, lineCount, speed, scale, strokeColour, strokeWidth])

  return (
    <canvas
      ref={canvasRef}
      className={`pointer-events-none absolute inset-0 z-[3] h-full w-full ${className}`}
      aria-hidden="true"
    />
  )
}
